import {
  CtaBuilderFn,
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import articlePage from "@faire/web-api--source/routes/support/articles/articleId";
import React, { type JSX } from "react";

import {
  MessageRetailerOrBrandButton,
  additionalDataRequiredForContactingRetailerOrBrandCTA,
} from "../../shared/instantAnswerUtils";

import { CreateReturnCTA } from "./ctas";
import { NEED_MORE_INFO_ANSWER } from "./shared";

export const CreateReturnIA: InstantAnswerBuilderFn = (strictLocalize) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription();
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.label;
      },
      linkBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.getLink?.(
          context.brandOrder
        );
      },
    },
    secondaryCtaBuilder: {
      textBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.label;
      },
      onClickFunctionBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.onClick;
      },
    },
    additionalRequiredData:
      additionalDataRequiredForContactingRetailerOrBrandCTA(),
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const { brandOrder, shippingContext } = context;
  if (!brandOrder || !shippingContext) {
    return NEED_MORE_INFO_ANSWER(strictLocalize);
  }

  return shippingContext.is_return_eligible
    ? RETURN_ELIGIBLE(strictLocalize)
    : RETURN_INELIGIBLE(strictLocalize);
};

const getPrimaryCTA: CtaBuilderFn = (context, strictLocalize) => {
  const { brandOrder, shippingContext } = context;
  if (!brandOrder || !shippingContext || !shippingContext.is_return_eligible) {
    return undefined;
  }

  return CreateReturnCTA(strictLocalize);
};

const RETURN_ELIGIBLE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => ELIGIBLE_FOR_FREE_RETURN(strictLocalize),
  getDescription: () => [FREE_RETURNS_FOR_60_DAYS(strictLocalize)],
});

const RETURN_INELIGIBLE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => NOT_ELIGIBLE_FOR_FREE_RETURN(strictLocalize),
  getDescription: () => (
    <Typography>
      <StrictLocalMsg
        defaultMessage="Free returns are only eligible on your first order with a brand and must be initiated within 60 days from the order date. <CertainProductLinkFormatting>Certain products</CertainProductLinkFormatting> are also ineligible for returns, such as seasonal products. Read more about our <ReturnPolicyLinkFormatting>return policy</ReturnPolicyLinkFormatting> or message the brand to discuss alternative options."
        values={{
          CertainProductLinkFormatting: LinkFormatting(
            articlePage("360007069352")
          ),
          ReturnPolicyLinkFormatting: LinkFormatting(
            articlePage("360002924031")
          ),
        }}
      />
    </Typography>
  ),
});

const ELIGIBLE_FOR_FREE_RETURN = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "You’re eligible for a free return!",
    description: {
      text: "Title for the answer of create a return issue if the selected order is return eligible",
    },
  });

const FREE_RETURNS_FOR_60_DAYS = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "We offer free returns for 60 days from the date the order is shipped on the first order you place with any brand. Create a return or read more about our return policy by checking out the recommended article from our Help Center below.",
  });

const NOT_ELIGIBLE_FOR_FREE_RETURN = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "This order isn’t eligible for a return",
    description: {
      text: "Title for the answer of create a return issue if the selected order is not return eligible",
    },
  });

const LinkFormatting = (linkTo: string) =>
  function (chunks: JSX.Element) {
    return (
      <Button linkTo={linkTo} variant="tertiary">
        {chunks}
      </Button>
    );
  };
